<template>
  <div :class="[
    $style.artistCard,
    $style.clickable,
    { [$style.myCard]: isMyCard },
    { [$style.softVariant]: variant === 'soft' },
    { [$style.compact]: compact },
  ]" @click="handleCardClick" @blur="removeHover" tabindex="0">
    <!-- 내 아티스트 배지 -->
    <div v-if="isMyCard" :class="$style.myBadge">
      <span>👑 내 아티스트 카드</span>
    </div>
    <div :class="$style.artistInfo">
      <!-- 프로필 사진 섹션 -->
      <div :class="$style.profileSection">
        <ProfileImageUploader :artist-name="artist.name" :profile-image="artist.profileImage" :artist-type="artist.type"
          :show-controls="false" />

        <div :class="$style.artistDetails">
          <div :class="$style.nameRow">
            <h3 :class="$style.artistName">{{ artist.name }}</h3>
            <!-- 소속 밴드 정보 -->
            <div v-if="artist.affiliatedBands && artist.affiliatedBands.length > 0" :class="$style.bandInfo">
              <span v-for="band in artist.affiliatedBands" :key="band" :class="$style.bandTag" :title="`${band} 소속`">
                🎸 {{ band }}
              </span>
            </div>
          </div>
          <h5 v-if="!roleBadge" :class="$style.artistType">
            {{ formatArtistType(artist.type) }}
          </h5>
          <div v-if="roleBadge" :class="$style.roleBadgeContainer">
            <span :class="$style.roleBadge">{{ roleBadge }}</span>
          </div>
        </div>

        <!-- 찜 버튼 -->
        <div :class="$style.likeButtonContainer">
          <button v-if="showLikeButton" :class="[$style.likeButton, { [$style.liked]: isLiked }]"
            @click.stop="handleLikeClick" :disabled="disabled">
            <img v-if="isLiked" :class="$style.likeHeart" src="@/assets/images/heart.png" alt="찜됨" />
            <img v-else :class="$style.likeHeart" src="@/assets/images/heart_gray.png" alt="찜하기" />
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ProfileImageUploader from "@/components/ProfileImageUploader.vue";
import { useUserStore } from "@/store/user";
import { getArtistTypeDisplayName } from "@/utils/artistTypes";

export default {
  name: "ArtistCard",
  components: {
    ProfileImageUploader,
  },
  props: {
    artist: {
      type: Object,
      required: true,
      validator(artist) {
        return artist && typeof artist.name === "string";
      },
    },
    isLiked: {
      type: Boolean,
      default: false,
    },
    showLikeButton: {
      type: Boolean,
      default: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    isMyCard: {
      type: Boolean,
      default: false,
    },
    roleBadge: {
      type: String,
      default: "",
    },
    variant: {
      type: String,
      default: "default",
      validator: (v) => ["default", "soft"].includes(v),
    },
    compact: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["like-toggle"],
  computed: {
    userStore() {
      return useUserStore();
    },
  },
  methods: {
    formatArtistType(type) {
      return getArtistTypeDisplayName(type);
    },

    handleCardClick(event) {
      // 아티스트 상세보기 페이지로 이동 (slug 우선)
      const artistRouteKey = this.artist.slug ?? this.artist.id;
      if (artistRouteKey == null) return;
      this.$router.push(`/artist/${artistRouteKey}`);
      // 클릭 후 포커스 제거하여 파란 음영 없애기
      event.target.blur();
    },
    handleLikeClick() {
      if (!this.userStore.isLogin) {
        this.userStore.showLoginPopup('artistLike');
        return;
      }
      this.$emit("like-toggle", this.artist.id);
    },
    removeHover() {
      // 포커스가 제거될 때 호버 상태도 제거
    },
  },
};
</script>

<style module>
.artistCard {
  display: grid;
  grid-template-columns: 1fr;
  padding: 10px;
  background: white;
  border-radius: 10px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  position: relative;
  overflow: visible;
}

.artistCard:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
}

.artistCard.clickable {
  cursor: pointer;
}

.artistCard.clickable:hover {
  transform: translateY(-3px);
  box-shadow: 0 6px 25px rgba(102, 189, 255, 0.2);
  border: 1px solid rgba(102, 189, 255, 0.3);
}

/* 감성적 VideoDetail용 soft variant */
.artistCard.softVariant {
  background: rgba(255, 255, 255, 0.6);
  border-radius: 16px;
  box-shadow: none;
  border: 1px solid rgba(139, 107, 184, 0.12);
}

.artistCard.softVariant:hover {
  background: rgba(255, 255, 255, 0.85);
  box-shadow: none;
  border-color: rgba(139, 107, 184, 0.2);
}

.artistCard.softVariant .artistName {
  color: #2d1b4e;
}

.artistCard.softVariant .artistType {
  color: #7b6b94;
}

.artistCard.softVariant .roleBadge {
  background: rgba(139, 107, 184, 0.12);
  border-color: rgba(139, 107, 184, 0.2);
  color: #5d4a7a;
}

.artistCard.compact {
  padding: 0;
}

.artistCard.clickable:focus {
  outline: none;
}

/* 내 아티스트 카드 특별 스타일 */
.myCard {
  background: #ffffff;
  position: relative;
}

.myCard::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(45deg,
      #ffd700,
      #ffed4e,
      #ff1493,
      #ff69b4,
      #00d4ff,
      #1e90ff,
      #ffd700);
  border-radius: 10px;
  z-index: 0;
  background-size: 300% 300%;
  animation: gradientShift 5s ease infinite;
  pointer-events: none;
  padding: 3px;
  -webkit-mask:
    linear-gradient(#fff 0 0) content-box,
    linear-gradient(#fff 0 0);
  -webkit-mask-composite: xor;
  mask:
    linear-gradient(#fff 0 0) content-box,
    linear-gradient(#fff 0 0);
  mask-composite: exclude;
}

.myCard::after {
  content: "";
  position: absolute;
  top: 3px;
  left: 3px;
  right: 3px;
  bottom: 3px;
  background: #ffffff;
  border-radius: 7px;
  z-index: 1;
  pointer-events: none;
}

.myCard>* {
  position: relative;
  z-index: 2;
}

.myCard:hover {
  transform: translateY(-5px) scale(1.02);
  box-shadow: 0 8px 30px rgba(255, 215, 0, 0.4);
}

@keyframes gradientShift {
  0% {
    background-position: 0% 50%;
  }

  50% {
    background-position: 100% 50%;
  }

  100% {
    background-position: 0% 50%;
  }
}


.myBadge {
  position: absolute;
  top: -6px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10;
  background: linear-gradient(135deg, #ffd700 0%, #ffed4e 100%);
  color: #333;
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 10px;
  font-weight: bold;
  font-family: "NanumSquareRoundB", sans-serif;
  box-shadow: 0 2px 8px rgba(255, 215, 0, 0.4);
  white-space: nowrap;
  border: 2px solid #fff;
}

.myBadge span {
  display: flex;
  align-items: center;
  gap: 4px;
}

.artistInfo {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.profileSection {
  display: flex;
  align-items: center;
  flex-direction: column;
  text-align: center;
}

.artistDetails {
  flex: 1;
  display: flex;
  flex-direction: column;
  text-align: left;
  gap: 8px;
  margin: auto;
  width: 100%;
}

.nameRow {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  flex-wrap: wrap;
  width: 100%;
}

.artistName {
  margin: 0;
  font-size: 0.95rem;
  color: #333;
  text-align: center;
  font-family: "NanumSquareRoundEB", sans-serif;
  flex-shrink: 0;
}

.artistType {
  margin: 0;
  font-size: 0.7rem;
  color: #666;
  font-family: "NanumSquareRoundB", sans-serif;
  text-align: center;
  font-style: normal;
}

.roleBadge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px 8px;
  border-radius: 999px;
  background: #f5f7fb;
  border: 1px solid #dbe2ee;
  color: #4b5a73;
  font-size: 0.62rem;
  line-height: 1.3;
  font-family: "NanumSquareRoundB", sans-serif;
}

.roleBadgeContainer {
  width: 100%;
  display: flex;
  justify-content: center;
  margin-top: 2px;
}

.bandInfo {
  display: flex;
  align-items: center;
  gap: 3px;
  flex-wrap: wrap;
  justify-content: center;
}

.bandTag {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #fff;
  padding: 2px 6px;
  border-radius: 12px;
  font-size: 0.6rem;
  font-family: "NanumSquareRoundB", sans-serif;
  box-shadow: 0 2px 6px rgba(102, 126, 234, 0.3);
  white-space: nowrap;
  transition: all 0.2s ease;
  cursor: default;
  line-height: 14px;
}

.bandTag:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 10px rgba(102, 126, 234, 0.4);
}

.linkHint {
  margin: 4px 0 0 0;
  font-size: 0.65rem;
  color: #66bdff;
  font-family: "NanumSquareRoundR", sans-serif;
  opacity: 0.8;
  transition: opacity 0.2s ease;
  text-align: center;
}

.clickable:hover .linkHint {
  opacity: 1;
  color: #4a9eff;
}

.likeButtonContainer {
  margin: auto;
}

/* 찜 버튼 스타일 */
.likeButton {
  background: none;
  /* border: 1px solid #ccc; */
  border: none;
  cursor: pointer;
  padding: 8px;
  border-radius: 50%;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
}

.likeHeart {
  width: 22px;
  height: 22px;
  transition: all 0.2s ease;
}

.likeButton:hover {
  background: rgba(255, 107, 157, 0.1);
  transform: scale(1.1);
}

.likeButton:hover .likeHeart {
  transform: scale(1.1);
}

.likeButton.liked {
  animation: heartBeat 0.6s ease-in-out;
}

.likeButton:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

@keyframes heartBeat {
  0% {
    transform: scale(1);
  }

  14% {
    transform: scale(1.3);
  }

  28% {
    transform: scale(1);
  }

  42% {
    transform: scale(1.3);
  }

  70% {
    transform: scale(1);
  }
}

/* ==============================================
   반응형 디자인
   - Medium: 481px ~ (기본 스타일, 최대 1024px)
   - Small: ~480px
   ============================================== */

/* Small (~480px) - 모바일 */
@media screen and (max-width: 480px) {

  .profileSection {
    flex-direction: row;
  }

  .myBadge {
    font-size: 9px;
    padding: 2px 8px;
  }

  .nameRow {
    gap: 6px;
  }

  .bandTag {
    font-size: 0.55rem;
    padding: 1px 7px;
  }
}
</style>
